import { createContext, createElement, useContext, useEffect, useState, type ReactNode } from 'react'

/** Wie oft nachgesehen wird, ob die Uhr des Geräts gewandert ist. */
export const NACHSEHEN_MS = 20_000

/**
 * Der Versatz des Geräts, als Context.
 *
 * **Und ausdrücklich ein Context und kein Rückgabewert** — das ist die
 * Behebung, nicht die Verpackung. Der Haken sass zuerst allein in `Shell`, und
 * das hat nur die halbe Anwendung erreicht: react-router gibt dem `<Outlet/>`
 * bei jedem Zeichnen **dasselbe Element-Objekt** zurück, und React überspringt
 * einen Teilbaum, dessen Element sich nicht geändert hat. Der Zustandswechsel
 * in `Shell` blieb damit an der Route hängen.
 *
 * Gemessen, nebeneinander im selben Fenster, nach einem Zonenwechsel:
 *
 * ```
 * Liste (unter Shell):     19:26 → 22:56
 * Sprechblase (im Outlet): 19:26 → 19:26
 * ```
 *
 * Ein Context kennt diese Sperre nicht: er benachrichtigt seine Leser
 * unmittelbar, gleich wie viele bailouts dazwischen liegen. Genau dafür gibt
 * es ihn.
 */
const Uhr = createContext(0)

/**
 * Merkt, wenn das Gerät seine Zeitzone wechselt — **und zeichnet dann neu**.
 *
 * **Die Formatierer allein genügen nicht.** `lib/format.ts` baut sie seit
 * 0.21.1 neu, sobald sich der Versatz geändert hat; gefragt werden sie aber
 * nur beim Zeichnen. Kommt kein Neuzeichnen, steht die alte Uhrzeit weiter da.
 *
 * **Und darauf war Verlass genau so lange, wie es zufällig gut ging.** Der
 * Prüflauf holte sich sein Neuzeichnen aus dem Nachladen der Nachrichten —
 * einmal grün, beim nächsten vollen Lauf rot, ohne dass sich am Code etwas
 * geändert hätte. Am Gerät ist es dieselbe Lücke: wer abends die Zone umstellt
 * und niemanden schreiben hat, liest weiter die Zeiten von vorhin.
 *
 * Drei Anlässe, und jeder hat einen Grund:
 *
 *  - **Zurück in die App** (`visibilitychange`). Das ist der Weg, den ein
 *    Mensch wirklich geht: in den Einstellungen die Zone stellen, dann
 *    zurückwechseln.
 *  - **Das Fenster bekommt den Fokus.** Am Rechner steht die App neben
 *    anderen Fenstern; dort wechselt die Sichtbarkeit nicht.
 *  - **Alle zwanzig Sekunden.** Für den Fall, dass niemand etwas tut: die
 *    Sommerzeit stellt um drei Uhr nachts um, und ein Telefon mit
 *    automatischer Zeitzone wechselt sie mitten auf der Fahrt.
 *
 * **Verglichen wird der Versatz und nicht der Zonenname** — dieselbe
 * Entscheidung wie in `lib/format.ts`, und aus demselben Grund: für eine
 * Uhrzeit reicht er, und er fängt die Sommerzeit mit. Bleibt er gleich, bleibt
 * auch der Wert gleich, und React zeichnet gar nicht erst neu.
 */
export function Uhrwechsel({ children }: { children: ReactNode }) {
  const [versatz, setVersatz] = useState(() => new Date().getTimezoneOffset())

  useEffect(() => {
    const sieh = () => {
      const jetzt = new Date().getTimezoneOffset()
      setVersatz((alt) => (alt === jetzt ? alt : jetzt))
    }
    const uhr = window.setInterval(sieh, NACHSEHEN_MS)
    document.addEventListener('visibilitychange', sieh)
    window.addEventListener('focus', sieh)
    return () => {
      window.clearInterval(uhr)
      document.removeEventListener('visibilitychange', sieh)
      window.removeEventListener('focus', sieh)
    }
  }, [])

  return createElement(Uhr.Provider, { value: versatz }, children)
}

/**
 * **Wer eine Uhrzeit in der Uhr des Geräts zeigt, ruft das hier auf.**
 *
 * Der Rückgabewert wird nirgends gelesen, und das ist der Punkt: er ändert
 * sich, wenn das Gerät die Zone wechselt, und damit zeichnet die Komponente
 * neu. `lib/format.ts` baut seine Formatierer dabei von selbst neu — ohne das
 * Neuzeichnen wird es aber nie danach gefragt.
 *
 * **Nicht nötig für Zeiten in der Uhr der Reise** (`lib/ortszeit.ts`): die
 * hängen an einem gespeicherten Versatz und nicht am Gerät.
 */
export function useUhrwechsel(): number {
  return useContext(Uhr)
}
